/** @format */

import {
	Avatar,
	Box,
	Container,
	Grid,
	Link,
	styled,
	Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { Footer, SEO } from "../allComponents/AllComponents";

/* ── Keyframe animations ─────────────────────────────────────── */
const fadeSlideUp = `
  @keyframes fadeSlideUp {
    from { opacity: 0; transform: translateY(32px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

const fadeIn = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
`;

/* ── Global keyframe injection ───────────────────────────────── */
const GlobalStyles = styled("style")({});

/* ── Hero ────────────────────────────────────────────────────── */
const HeroSection = styled(Box)(({ theme }) => ({
	background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #2563eb 100%)",
	color: "#fff",
	padding: theme.spacing(14, 4, 10),
	textAlign: "center",
	position: "relative",
	overflow: "hidden",
	"&::after": {
		content: '""',
		position: "absolute",
		bottom: 0,
		left: 0,
		right: 0,
		height: 60,
		background: theme.palette.background.default,
		clipPath: "ellipse(55% 100% at 50% 100%)",
	},
}));

const HeroTitle = styled(Typography)({
	fontFamily: "'Montserrat', sans-serif",
	fontWeight: 800,
	fontSize: "clamp(2rem, 5vw, 3.5rem)",
	letterSpacing: "-0.5px",
	animation: "fadeSlideUp 0.7s ease both",
});

const HeroSubtitle = styled(Typography)({
	opacity: 0.85,
	maxWidth: 600,
	margin: "16px auto 0",
	fontSize: "1.1rem",
	animation: "fadeSlideUp 0.7s 0.15s ease both",
});

/* ── Page wrapper ────────────────────────────────────────────── */
const PageContainer = styled(Container)(({ theme }) => ({
	paddingTop: theme.spacing(8),
	paddingBottom: theme.spacing(10),
	fontFamily: "'Inter', sans-serif",
}));

/* ── Animated section ────────────────────────────────────────── */
const Section = styled(Box)<{ delay?: string }>(({ delay = "0s" }) => ({
	marginBottom: 64,
	animation: `fadeSlideUp 0.65s ${delay} ease both`,
}));

const SectionHeading = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontWeight: 700,
	fontSize: "1.5rem",
	marginBottom: theme.spacing(2),
	position: "relative",
	display: "inline-block",
	"&::after": {
		content: '""',
		display: "block",
		marginTop: 6,
		height: 3,
		width: 40,
		borderRadius: 2,
		background: "linear-gradient(90deg, #2563eb, #60a5fa)",
	},
}));

/* ── Value cards ─────────────────────────────────────────────── */
const ValuesGrid = styled(Box)(({ theme }) => ({
	display: "grid",
	gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
	gap: theme.spacing(3),
	marginTop: theme.spacing(3),
}));

const ValueCard = styled(Box)(({ theme }) => ({
	background: theme.palette.mode === "dark" ? "#1e293b" : "#f8fafc",
	border: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"}`,
	borderRadius: 16,
	padding: theme.spacing(3),
	transition: "transform 0.25s ease, box-shadow 0.25s ease",
	"&:hover": {
		transform: "translateY(-4px)",
		boxShadow: "0 12px 32px rgba(37,99,235,0.12)",
	},
}));

const ValueTitle = styled(Typography)({
	fontWeight: 700,
	fontSize: "0.95rem",
	color: "#2563eb",
	marginBottom: 6,
	textTransform: "uppercase",
	letterSpacing: "0.05em",
});

/* ── Team card ───────────────────────────────────────────────── */
const TeamCard = styled(Box)(({ theme }) => ({
	textAlign: "center",
	padding: theme.spacing(4, 3),
	borderRadius: 20,
	background: theme.palette.mode === "dark" ? "#1e293b" : "#fff",
	boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
	border: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"}`,
	transition: "transform 0.3s ease, box-shadow 0.3s ease",
	animation: "fadeIn 0.8s 0.4s ease both",
	"&:hover": {
		transform: "translateY(-6px)",
		boxShadow: "0 16px 48px rgba(37,99,235,0.15)",
	},
}));

const StyledAvatar = styled(Avatar)({
	width: 120,
	height: 120,
	margin: "0 auto 16px",
	border: "4px solid #2563eb",
	boxShadow: "0 0 0 4px rgba(37,99,235,0.15)",
	transition: "transform 0.3s ease",
	"&:hover": {
		transform: "scale(1.05)",
	},
});

/* ── Contact ─────────────────────────────────────────────────── */
const ContactBox = styled(Box)(({ theme }) => ({
	background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
	border: "1px solid #bfdbfe",
	borderRadius: 16,
	padding: theme.spacing(4),
	marginTop: theme.spacing(2),
}));

/* ─────────────────────────────────────────────────────────────── */

const VALUES_KEYS = [
	"transparency",
	"security",
	"community",
	"accessibility",
	"innovation",
	"integrity",
] as const;

const AboutUs = () => {
	const { t } = useTranslation("pages");

	return (
		<React.Fragment>
			<GlobalStyles>{`${fadeSlideUp}${fadeIn}`}</GlobalStyles>
			<SEO
				title={t("about.title")}
				description="Learn about FundsyFi's mission to build trust-driven financial tools  from community savings circles (FundsyFi Circle) to accounts receivable recovery for businesses (FundsyFi Recover). Meet our team and discover our values."
				keywords="about FundsyFi, savings circles, accounts receivable recovery, customer accountability, community finance platform, financial technology, FundsyFi Circle, FundsyFi Recover"
				url="/about-us"
			/>

			{/* ── Hero ── */}
			<HeroSection>
				<HeroTitle variant="h1">{t("about.title")}</HeroTitle>
				<HeroSubtitle variant="body1">
					{t("about.mission.description")}
				</HeroSubtitle>
			</HeroSection>

			<PageContainer maxWidth="md">

				{/* ── Story ── */}
				<Section delay="0.05s">
					<SectionHeading variant="h2">
						{t("about.story.heading")}
					</SectionHeading>
					<Typography variant="body1" paragraph sx={{ lineHeight: 1.8, color: "text.secondary" }}>
						{t("about.story.paragraph1")}
					</Typography>
					<Typography variant="body1" paragraph sx={{ lineHeight: 1.8, color: "text.secondary" }}>
						{t("about.story.paragraph2")}
					</Typography>
					<Typography variant="body1" paragraph sx={{ lineHeight: 1.8, color: "text.secondary" }}>
						{t("about.story.paragraph3")}
					</Typography>
				</Section>

				{/* ── Values ── */}
				<Section delay="0.1s">
					<SectionHeading variant="h2">
						{t("about.values.heading")}
					</SectionHeading>
					<ValuesGrid>
						{VALUES_KEYS.map((key) => (
							<ValueCard key={key}>
								<ValueTitle>{t(`about.values.${key}.title`)}</ValueTitle>
								<Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.7 }}>
									{t(`about.values.${key}.description`)}
								</Typography>
							</ValueCard>
						))}
					</ValuesGrid>
				</Section>

				{/* ── Team ── */}
				<Section delay="0.15s">
					<SectionHeading variant="h2">
						{t("about.team.heading")}
					</SectionHeading>
					<Grid container spacing={4} sx={{ mt: 1 }}>
						{[
							{
								name: "Paul Ndam",
								role: "Founder",
								avatar:
									"https://imagefile-bucket.s3.us-east-2.amazonaws.com/00bb6279-04da-4dfe-a39b-d918a4bf2504.jpeg",
							},
						].map((member) => (
							<Grid item xs={12} sm={6} md={4} key={member.name}>
								<TeamCard>
									<StyledAvatar alt={member.name} src={member.avatar} />
									<Typography
										variant="h6"
										sx={{ fontWeight: 700, fontFamily: "'Montserrat', sans-serif" }}>
										{member.name}
									</Typography>
									<Typography
										variant="body2"
										sx={{
											mt: 0.5,
											color: "#2563eb",
											fontWeight: 600,
											textTransform: "uppercase",
											letterSpacing: "0.08em",
											fontSize: "0.78rem",
										}}>
										{member.role}
									</Typography>
								</TeamCard>
							</Grid>
						))}
					</Grid>
				</Section>

				{/* ── Contact ── */}
				<Section delay="0.2s">
					<SectionHeading variant="h2">
						{t("about.contact.heading")}
					</SectionHeading>
					<ContactBox>
						<Typography variant="body1" sx={{ lineHeight: 1.8, color: "text.secondary" }}>
							{t("about.contact.description")}{" "}
							<Link
								href={`mailto:${t("about.contact.email")}`}
								sx={{ color: "#2563eb", fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
								{t("about.contact.email")}
							</Link>{" "}
							or follow us on social media for updates and opportunities.
						</Typography>
					</ContactBox>
				</Section>

			</PageContainer>
			<Footer />
		</React.Fragment>
	);
};

export default AboutUs;
